import { assert } from 'assert-ts';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { handleServerFormError, InternalServerError } from '#api/errors.ts';
import { useAddRole } from '#api/roles.queries.ts';
import { AddButton } from '#components/atoms/buttons/add-button/AddButton.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { Role } from '#dn-types/role.ts';
import { BlockNavigationDialog } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { mapAPIListErrorsToFormListErrors } from '#util/map-api-list-errors-to-form-list-errors.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { roleDescriptionValidation, roleNameValidation } from '../../validations/role-validations.ts';
import { AddFirewallRuleModal } from '../add-firewall-rule-modal/AddFirewallRuleModal.tsx';
import { EditFirewallRuleModal } from '../edit-firewall-rule-modal/EditFirewallRuleModal.tsx';
import { FirewallRuleList } from '../firewall-rule-list/FirewallRuleList.tsx';
import { sortFirewallRules } from '../firewall-rule-list/sortFirewallRules.ts';
import type { FirewallRuleFieldValue, FormData } from '../types.ts';
import { mapFirewallRulesToRoleForm, mapFormValuesToFirewallRules } from './mapFirewallRulesToRoleForm.ts';
import { mapRoleToForm } from './mapRoleToForm.ts';
import { useEditRole, useUpdateRoleCache } from './RoleDetailForm.queries.ts';
import styles from './RoleDetailForm.module.css';

const DEFAULT_RULES = [
  {
    protocol: 'ICMP',
    portRange: null,
    description: 'Allows ping requests from other hosts',
    allowedRoleID: null,
  } as const,
];

type EditProps = {
  roleId: string;
  defaultValues: Role;
  /** Function to execute after a successful save operation. */
  onSave?: () => void;
};

type AddProps = {
  roleId: null;
  /** Prefills the form, e.g. when duplicating an existing role */
  defaultValues?: FormData;
  /** Function to execute after a successful save operation. */
  onSave?: () => void;
};

const DEFAULT_VALUES = {
  roleName: '',
  roleDescription: '',
  firewallRules: mapFirewallRulesToRoleForm(DEFAULT_RULES),
};

type Props = EditProps | AddProps;

export function RoleDetailForm(props: Props) {
  const { roleId, onSave } = props;
  const defaultValues = (
    props.roleId === null ?
      (props.defaultValues ?? DEFAULT_VALUES)
    : mapRoleToForm(props.defaultValues)) satisfies FormData;
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  // null = closed; defaultValues prefill the add modal when duplicating an existing rule
  const [addRuleModal, setAddRuleModal] = useState<{ defaultValues?: FirewallRuleFieldValue } | null>(null);
  const [blockNavigation, setBlockNavigation] = useState(false);
  const { register, handleSubmit, setError, control, formState } = useForm<FormData>({
    defaultValues,
  });
  const {
    fields: firewallRuleFields,
    remove,
    append,
    update,
  } = useFieldArray({
    control,
    name: 'firewallRules',
  });
  const editRoleMutation = useEditRole();
  const addRoleMutation = useAddRole();
  const updateRoleCache = useUpdateRoleCache();
  const { errors, isDirty, isSubmitting, isSubmitted } = formState;
  const isValid = checkIsFormValid(errors);
  // We are going to sort the rules before passing them to the table, but we need to operate on them
  // based on their original indices, due to react-hook-form APIs.
  const ruleIndices = new Map(firewallRuleFields.map(({ id }, index) => [id, index]));

  // Prompt before navigation if form is dirty
  // Note: this is disabled during submission
  useEffect(() => {
    setBlockNavigation(isDirty);
  }, [isDirty]);

  const isEditing = Boolean(roleId);

  const onSubmit = handleSubmit(async (data) => {
    setBlockNavigation(false);
    // Our select box requires string values, but we want to submit it with `null`
    const firewallRules = mapFormValuesToFirewallRules(data.firewallRules);

    try {
      if (roleId) {
        const updatedRole = await editRoleMutation.mutateAsync({
          id: roleId,
          values: {
            description: data.roleDescription,
            firewallRules,
          },
        });
        updateRoleCache(updatedRole);
      } else {
        const newRole = await addRoleMutation.mutateAsync({
          name: data.roleName,
          description: data.roleDescription,
          firewallRules,
        });
        updateRoleCache(newRole);
      }

      onSave?.();
    } catch (error) {
      if (error instanceof InternalServerError) {
        setError('root.global', { message: 'We couldn’t save your changes, please try again' });
      } else {
        handleServerFormError(error, ({ path, message }) => {
          if (path?.startsWith('firewallRules[')) {
            const mappedFieldPath = mapAPIListErrorsToFormListErrors('firewallRules', path);

            setError(mappedFieldPath, { message }, { shouldFocus: true });
            return true;
          }

          switch (path) {
            case 'name':
              setError('roleName', { message }, { shouldFocus: true });
              return true;
            case 'description':
              setError('roleDescription', { message }, { shouldFocus: true });
              return true;
            case 'firewallRules':
              setError('firewallRules', { message }, { shouldFocus: true });
              return true;
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      }
    }
  });

  const sortedRules = useMemo(() => sortFirewallRules(firewallRuleFields), [firewallRuleFields]);

  return (
    <>
      <BlockNavigationDialog when={blockNavigation} title="Unsaved changes">
        You have unsaved changes, are you sure you want to leave?
      </BlockNavigationDialog>
      <form method="post" id="role-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="wide" className="mb-40">
          {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
          <Field label="Role name" className="width-100">
            <Input
              className="width-100"
              maxLength={50}
              hasError={Boolean(errors.roleName)}
              {...register('roleName', { ...roleNameValidation, disabled: isEditing })}
            />
            {errors.roleName && <ErrorMessage>{errors.roleName.message}</ErrorMessage>}
          </Field>
          <Field label="Description" className="width-100">
            <Input
              maxLength={255}
              hasError={Boolean(errors.roleDescription)}
              {...register('roleDescription', roleDescriptionValidation)}
              className="width-100"
            />
            {errors.roleDescription && <ErrorMessage>{errors.roleDescription.message}</ErrorMessage>}
          </Field>
        </Spacer>

        <div className="h4 mb-4">Inbound firewall rules</div>
        <HelpText className="mb-16">
          Inbound traffic is denied by default. Add rules to allow traffic from hosts belonging to specific roles or
          tags.
        </HelpText>

        {errors.firewallRules?.root && (
          <ErrorMessage className="mb-24">{errors.firewallRules.root.message}</ErrorMessage>
        )}
        {errors.firewallRules?.message && <ErrorMessage className="mb-24">{errors.firewallRules.message}</ErrorMessage>}
        {Array.isArray(errors.firewallRules) ?
          <div className="mb-24">
            {errors.firewallRules
              .map((error, index) =>
                error?.message ?
                  // eslint-disable-next-line react/no-array-index-key -- errors are positional and ephemeral
                  <ErrorMessage key={index}>{error.message}</ErrorMessage>
                : null
              )
              .filter((error) => Boolean(error))}
          </div>
        : null}
        <FirewallRuleList
          className={styles.RoleDetailForm_firewallTable}
          firewallRules={sortedRules}
          hideOverflowX={true}
          onRemove={(id) => {
            remove(assert(ruleIndices.get(id)));
          }}
          onEdit={(id) => {
            setEditingRuleIndex(assert(ruleIndices.get(id)));
          }}
          onDuplicate={(id) => {
            const rule = assert(firewallRuleFields[assert(ruleIndices.get(id))], 'expected duplicated rule to exist');
            setAddRuleModal({ defaultValues: { ...rule, id: '' } });
          }}
        />
        <AddButton
          className={styles.RoleDetailForm_addFirewallRuleButton}
          onClick={() => {
            setAddRuleModal({});
          }}
        >
          Add firewall rule
        </AddButton>

        <ConfirmChangesBanner
          isOpen={isDirty}
          title={isSubmitted && !isValid ? 'Unable to save.  Please update and try again.' : undefined}
          primaryButton={
            <Button variant="primary" type="submit" disabled={isSubmitting || (isEditing && !isDirty)}>
              {isEditing ? 'Save role' : 'Add role'}
            </Button>
          }
          secondaryButton={<CancelLinkButton fallbackTo="/roles" />}
        />
      </form>
      {editingRuleIndex != null ?
        <EditFirewallRuleModal
          firewallRule={assert(
            firewallRuleFields[editingRuleIndex],
            'expected editingRuleIndex to find firewall rules'
          )}
          onSave={(values) => {
            update(assert(ruleIndices.get(values.id)), values);
            setEditingRuleIndex(null);
          }}
          onClose={() => {
            setEditingRuleIndex(null);
          }}
        />
      : null}
      {addRuleModal && (
        <AddFirewallRuleModal
          defaultValues={addRuleModal.defaultValues}
          onSave={(values) => {
            append(values);
            setAddRuleModal(null);
          }}
          onClose={() => {
            setAddRuleModal(null);
          }}
        />
      )}
    </>
  );
}
